import React, { useCallback, useEffect, useState } from 'react';
import { Clock, Loader2, Check } from 'lucide-react';
import { api } from '../../lib/api';

// Weekly open hours for ONE office — the practice-level schedule every
// provider there inherits (062). Used by Settings → Hours and inline in the
// referral inbox, where a clinic accepting a referral sets the office's
// hours on the spot so the patient (or the firm) has real slots to book.

export const DAYS = [
  ['monday', 'Monday'], ['tuesday', 'Tuesday'], ['wednesday', 'Wednesday'], ['thursday', 'Thursday'],
  ['friday', 'Friday'], ['saturday', 'Saturday'], ['sunday', 'Sunday'],
];
const SHORT = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
const DEFAULT_BLOCK = { start_time: '09:00', end_time: '17:00' };
const inputClass = 'w-[118px] px-2 py-[7px] bg-tertiary border border-strong rounded-[8px] text-primary-c text-[12.5px] font-[inherit] focus:outline-none focus:border-[#0DA7CA]';

export function gridFrom(rows) {
  const g = {};
  for (const [d] of DAYS) g[d] = null;
  for (const r of rows || []) g[r.day_of_week] = { start_time: r.start_time.slice(0, 5), end_time: r.end_time.slice(0, 5) };
  return g;
}

const fmtT = (t) => {
  const [h, m] = t.split(':').map(Number);
  const ap = h >= 12 ? 'PM' : 'AM';
  return `${((h + 11) % 12) + 1}${m ? `:${String(m).padStart(2, '0')}` : ''} ${ap}`;
};

// "Mon–Fri 9 AM–5 PM · Sat 9 AM–1 PM" from hour rows
export function summarizeHours(rows) {
  if (!rows || !rows.length) return null;
  const g = gridFrom(rows);
  const groups = [];
  for (const [d] of DAYS) {
    const r = g[d];
    if (!r) continue;
    const key = `${r.start_time}-${r.end_time}`;
    const last = groups[groups.length - 1];
    if (last && last.key === key && last.endIdx === DAYS.findIndex(([x]) => x === d) - 1) { last.end = d; last.endIdx += 1; }
    else groups.push({ key, start: d, end: d, endIdx: DAYS.findIndex(([x]) => x === d), r });
  }
  return groups.map((gr) => `${SHORT[gr.start]}${gr.start !== gr.end ? `–${SHORT[gr.end]}` : ''} ${fmtT(gr.r.start_time)}–${fmtT(gr.r.end_time)}`).join(' · ');
}

export default function OfficeHoursEditor({ orgId, locationId, officeName, canEdit = true, compact = false, onSaved, initialRows }) {
  const [grid, setGrid] = useState(() => gridFrom(initialRows));
  const [published, setPublished] = useState(!!(initialRows && initialRows.length));
  const [loading, setLoading] = useState(!initialRows);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!orgId || !locationId || initialRows) return;
    setLoading(true); setError(''); setSaved(false);
    try {
      const rows = await api.getLocationHours(orgId, locationId);
      setGrid(gridFrom(rows));
      setPublished((rows || []).length > 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [orgId, locationId, initialRows]);
  useEffect(() => { load(); }, [load]);

  const setDay = (day, patch) => { setGrid((g) => ({ ...g, [day]: patch })); setSaved(false); };
  const copyWeekdays = () => {
    const src = grid.monday || DEFAULT_BLOCK;
    setGrid((g) => ({ ...g, tuesday: { ...src }, wednesday: { ...src }, thursday: { ...src }, friday: { ...src } }));
    setSaved(false);
  };
  const preset = () => {
    const g = {};
    for (const [d] of DAYS) g[d] = ['saturday', 'sunday'].includes(d) ? null : { ...DEFAULT_BLOCK };
    setGrid(g); setSaved(false);
  };

  const save = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      const blocks = DAYS.filter(([d]) => grid[d]).map(([d]) => ({ day_of_week: d, ...grid[d] }));
      await api.setLocationHours(orgId, locationId, blocks);
      setPublished(blocks.length > 0);
      setSaved(true);
      onSaved?.(blocks);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-[12.5px] text-muted-c flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading hours…</p>;

  return (
    <div>
      {error && <div className="text-[12px] text-danger bg-danger-soft rounded-[10px] px-3.5 py-2.5 mb-3">{error}</div>}
      {!published && (
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <p className="text-[12px] text-muted-c">
            No hours published for {officeName || 'this office'} yet — booking uses 8:00 AM to 6:00 PM every day until you save a schedule.
          </p>
          {canEdit && <button onClick={preset} className="text-[11.5px] font-semibold text-accent-light hover:text-accent whitespace-nowrap">Start from Mon–Fri 9–5</button>}
        </div>
      )}
      <div className="rounded-[12px] border border-theme overflow-hidden">
        {DAYS.map(([d, label], i) => {
          const row = grid[d];
          return (
            <div key={d} className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-4 ${compact ? 'py-1.5' : 'py-2.5'} ${i > 0 ? 'border-t border-theme' : ''} ${row ? '' : 'bg-tertiary/40'}`}>
              <label className={`flex items-center gap-2.5 ${compact ? 'w-[96px]' : 'w-[120px]'} cursor-pointer`}>
                <input type="checkbox" checked={!!row} disabled={!canEdit} onChange={(e) => setDay(d, e.target.checked ? { ...DEFAULT_BLOCK } : null)} />
                <span className={`text-[13px] font-semibold ${row ? 'text-primary-c' : 'text-muted-c'}`}>{compact ? SHORT[d] : label}</span>
              </label>
              {row ? (
                <div className="flex items-center gap-2 text-[12px] text-muted-c">
                  <input type="time" value={row.start_time} disabled={!canEdit} onChange={(e) => setDay(d, { ...row, start_time: e.target.value })} className={inputClass} />
                  <span>to</span>
                  <input type="time" value={row.end_time} disabled={!canEdit} onChange={(e) => setDay(d, { ...row, end_time: e.target.value })} className={inputClass} />
                </div>
              ) : (
                <span className="text-[12px] text-muted-c">Closed</span>
              )}
              {d === 'monday' && row && canEdit && (
                <button onClick={copyWeekdays} className="ml-auto text-[11px] font-semibold text-accent-light hover:text-accent">Copy to Tue–Fri</button>
              )}
            </div>
          );
        })}
      </div>
      {canEdit && (
        <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
          <p className="text-[11px] text-muted-c">Times are in the office&apos;s local time. Existing appointments are not changed.</p>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-[10px] text-[12.5px] font-semibold text-white gradient-accent shadow-glow disabled:opacity-50 inline-flex items-center gap-1.5">
            {saving ? <Loader2 size={13} className="animate-spin" /> : saved ? <Check size={13} /> : <Clock size={13} />} {saved ? 'Saved' : `Save ${officeName || 'hours'}`}
          </button>
        </div>
      )}
    </div>
  );
}
